import React, { useCallback, useState } from 'react';
import { Button, FileButton, Group, Loader, Modal, Stack, Switch, Text, TextInput } from '@mantine/core';
import { IconCamera, IconUpload } from '@tabler/icons-react';
import { useGraphService } from '@repo/services';
import { formatDateForFilename } from '@repo/utils';
import {
  fetchPropertyRootFolderId,
  getOrCreateChildFolder,
  uploadFilesToFolder,
  uploadFilesToFolderWithGps,
  uploadFileWithMetadata
} from './driveUtils';
import { extractGpsMetadataJson } from './imageUtils';
import { LocationMetadata } from './types/LocationMetadata';

interface DrivePictureUploadProps {
  driveId: string;
  transactionId?: string; // required for metadata tagging
  division?: string;
  propertyName?: string; // used as fallback to find root folder
  picturesFolderName?: string; // default 'Pictures'
  onUploaded?: (successCount: number, failureCount: number) => void;
  icon?: 'camera' | 'upload';
  size?: number;
  disabled?: boolean;
  enableGpsMetadata?: boolean; // new: extract EXIF GPS into metadata column
  rootFolderId?: string; // NEW: allow caller to supply pre-resolved root folder id
}

const DEFAULT_PICTURES_FOLDER = 'Pictures';



/**
 * A camera/upload button component that captures or selects photos, optionally attaches GPS metadata from EXIF or the device geolocation, and uploads them to a Microsoft Graph Drive folder.
 *
 * Intercepts single generic image captures to open a rename modal before upload. Supports custom folder naming and optional GPS metadata embedding.
 */
export const DrivePictureUpload: React.FC<DrivePictureUploadProps> = ({
  driveId,
  transactionId,
  division,
  propertyName = '',
  picturesFolderName = DEFAULT_PICTURES_FOLDER,
  onUploaded,
  icon = 'camera',
  size = 36,
  disabled,
  enableGpsMetadata = true,
  rootFolderId: propRootFolderId // NEW
}) => {
  const { graphClient } = useGraphService();
  const [rootFolderId, setRootFolderId] = useState<string | null>(null);
  const [picturesFolderId, setPicturesFolderId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [folderForSingle, setFolderForSingle] = useState<string | null>(null);
  const [exifGpsJson, setExifGpsJson] = useState<string | null>(null);
  const [includeUserGps, setIncludeUserGps] = useState(true); // default on if we need GPS and none in EXIF
  const [userGpsJson, setUserGpsJson] = useState<string | null>(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [singleStatus, setSingleStatus] = useState<string | null>(null);
  const [fileButtonKey, setFileButtonKey] = useState<number>(() => Date.now());

  // Lazy ensure folder path
  const ensureFolders = useCallback(async () => {
    if (!graphClient || !transactionId) return null;
    if (picturesFolderId) return picturesFolderId;
    try {
      let rootId = propRootFolderId || rootFolderId; // prefer prop if provided
      if (!rootId) {
        rootId = await fetchPropertyRootFolderId(graphClient, driveId, division, transactionId, propertyName);
        setRootFolderId(rootId);
      } else if (!rootFolderId) {
        // sync internal state if prop provided but internal empty
        setRootFolderId(rootId);
      }
      if (!rootId) return null;
      const picsId = await getOrCreateChildFolder(graphClient, driveId, rootId, picturesFolderName);
      setPicturesFolderId(picsId);
      return picsId;
    } finally {
      // no initializing flag
    }
  }, [graphClient, transactionId, picturesFolderId, rootFolderId, propRootFolderId, driveId, division, propertyName, picturesFolderName]);

  // Sync internal state when a new rootFolderId prop is passed
  React.useEffect(() => {
    if (propRootFolderId && propRootFolderId !== rootFolderId) {
      setRootFolderId(propRootFolderId);
      setPicturesFolderId(null); // force re-create pictures folder under new root
    }
  }, [propRootFolderId, rootFolderId]);

  // Intercept logic: single file and either a generic image name OR a MOV video.
  const GENERIC_IMAGE_NAMES = new Set(['image.jpg', 'image.jpeg']);
  const isMovExtension = (name: string) => /\.mov$/i.test(name);
  const shouldInterceptSingle = (file: File, totalCount: number) => {
    if (totalCount !== 1) return false;
    const lower = file.name.toLowerCase();
    if (file.type.startsWith('image/')) {
      return GENERIC_IMAGE_NAMES.has(lower); // only generic camera snapshot
    }
    if (file.type.startsWith('video/')) {
      return isMovExtension(lower); // any single MOV video
    }
    return false;
  };

  const buildUserGpsMetadata = (fileName: string, pos: GeolocationPosition): string => {
    const payload: LocationMetadata = {
      gps: {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        ...(pos.coords.altitude != null ? { altitude: pos.coords.altitude } : {})
      },
      source: 'user',
      extractedAt: new Date().toISOString(),
      fileName
    };
    return JSON.stringify(payload);
  };

  const requestUserLocation = async (): Promise<string | null> => {
    if (!('geolocation' in navigator)) return null;
    setGettingLocation(true);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
      });
      return buildUserGpsMetadata(renameValue || (originalFile?.name || 'image.jpg'), pos);
    } catch {
      return null;
    } finally {
      setGettingLocation(false);
    }
  };

  const openSinglePreviewFlow = async (file: File) => {
    if (!graphClient || !transactionId) return;
    const folderId = await ensureFolders();
    if (!folderId) return;
    setFolderForSingle(folderId);
    let exif: string | null = null;
    if (file.type.startsWith('image/')) {
      exif = await extractGpsMetadataJson(file).catch(() => null);
    }
    setExifGpsJson(exif);
    setIncludeUserGps(!exif); // only relevant if exif absent
    const base = file.name.replace(/\.[^.]+$/, '');
    // For generic image names encourage rename; for MOV keep existing base
    const isGenericImage = file.type.startsWith('image/') && (base.toLowerCase() === 'image');
    setRenameValue(isGenericImage ? '' : base);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setOriginalFile(file);
    setPreviewModalOpen(true);
  };

  const resetSingleState = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setOriginalFile(null);
    setExifGpsJson(null);
    setUserGpsJson(null);
    setIncludeUserGps(true);
    setRenameValue('');
    setFolderForSingle(null);
    setSingleStatus(null);
    setPreviewModalOpen(false);
    setFileButtonKey(Date.now());
  };

  const performSingleUpload = async () => {
    if (!originalFile || !graphClient || !transactionId || !folderForSingle) return;
    setUploading(true);
    setSingleStatus('Preparing...');
    // Derive final name (preserve extension)
    const extMatch = originalFile.name.match(/\.[^.]+$/);
    const extension = extMatch ? extMatch[0] : '';
    let finalBase =
      (renameValue || originalFile.name.replace(/\.[^.]+$/, '')).trim() || "image" ;
    if (finalBase === "image") {
      finalBase = `${formatDateForFilename()}${propertyName ? ` - ${propertyName}` : ''}`;
    }
    const finalName = finalBase + extension.toLowerCase();
    const fileForUpload = finalName === originalFile.name ? originalFile : new File([originalFile], finalName, { type: originalFile.type });

    let locationJson: string | null = exifGpsJson;
    if (!locationJson && includeUserGps) {
      if (!userGpsJson) {
        const userJson = await requestUserLocation();
        setUserGpsJson(userJson);
        locationJson = userJson;
      } else {
        locationJson = userGpsJson;
      }
    }
    const meta: Record<string, any> = { TransactionId: transactionId };
    if (locationJson) meta.LocationMetadata = locationJson as unknown as LocationMetadata;
    await uploadFileWithMetadata(graphClient, driveId, folderForSingle, fileForUpload, meta, {
      onStatus: (s) => setSingleStatus(s),
      onComplete: (ok) => {
        setUploading(false);
        onUploaded?.(ok ? 1 : 0, ok ? 0 : 1);
        resetSingleState();
      }
    });
  };

  const handleChange = async (files:File[]) => {
    if (!files.length || !graphClient || !transactionId) return;
    // Intercept any single image capture for preview/rename/GPS (camera capture scenario)
    if (shouldInterceptSingle(files[0] as File, files.length)) {
      await openSinglePreviewFlow(files[0] as File);
      return; // wait for user confirmation
    }
    const folderId = await ensureFolders();
    if (!folderId) return;
    setUploading(true);
    let success = 0; let fail = 0;
    const callbacks = {
      onComplete: (_file: File, ok: boolean) => { if (ok) { success++; } else { fail++; } },
      afterAll: () => {
        setUploading(false);
        onUploaded?.(success, fail);
        setFileButtonKey(Date.now());
      }
    };
    if (enableGpsMetadata) {
      await uploadFilesToFolderWithGps(graphClient, driveId, folderId, files, transactionId, callbacks);
    } else {
      await uploadFilesToFolder(graphClient, driveId, folderId, files, { TransactionId: transactionId }, callbacks);
    }
  };

  const IconComponent = icon === 'camera' ? IconCamera : IconUpload;
  return (
    <>
      <FileButton key={fileButtonKey} multiple onChange={handleChange} accept="image/*,video/*">
        {(props) => (

          <Button size={'sm'} leftSection={uploading ? <Loader size="sm" /> : <IconComponent size={Math.round(size * 0.6)} />}
                  loading={uploading} {...props} variant="light"  color="blue" radius="xl" disabled={disabled || !transactionId || uploading}>
            Upload
          </Button>
        )}
      </FileButton>

      <Modal opened={previewModalOpen} onClose={() => { if (!uploading) resetSingleState(); }} title="Review Image" centered size="md" overlayProps={{ opacity: 0.55, blur: 3 }}>
        <Stack gap="sm">
          {previewUrl && (
            originalFile?.type.startsWith('video/') ? (
              <video src={previewUrl} controls style={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee' }} />
            ) : (
              <img src={previewUrl} alt="preview" style={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee' }} />
            )
          )}
          <TextInput label="File name" placeholder="Enter a descriptive name" value={renameValue} onChange={(e) => setRenameValue(e.currentTarget.value)} description="Extension will be preserved" disabled={uploading} />
          {exifGpsJson ? (
            <Text size="sm" c="green">GPS metadata detected from EXIF and will be attached.</Text>
          ) : (
            <Switch label="Include my current GPS location" checked={includeUserGps} onChange={(e) => setIncludeUserGps(e.currentTarget.checked)} disabled={uploading || gettingLocation} />
          )}
          {gettingLocation && <Text size="xs" c="dimmed">Obtaining location…</Text>}
          {singleStatus && <Text size="sm" c={singleStatus.startsWith('❌') ? 'red' : singleStatus.startsWith('✅') ? 'green' : undefined}>{singleStatus}</Text>}
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => { if (!uploading) resetSingleState(); }} disabled={uploading}>Cancel</Button>
            <Button onClick={performSingleUpload} loading={uploading} disabled={!originalFile}>Upload</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default DrivePictureUpload;
